import { Feature, FeatureCollection, GeoJsonObject, Geometry, Position } from 'geojson'
import L, { LatLngExpression, Layer, PathOptions } from 'leaflet'
import * as React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { GeoJSON, ImageOverlay, useMap, useMapEvents } from 'react-leaflet'
import { useHistory } from 'react-router-dom'

import {
  MAP_HIT_AREA_WEIGHT,
  MAP_OVERLAY_IMAGE_Z_INDEX,
  MAP_POINT_HIT_AREA_RADIUS,
  MAP_POINT_RADIUS,
  MAP_STAND_IN_POINT_OPTIONS
} from 'src/constants/map'
import {
  featureHoverOptions,
  featurePathOptions,
  isFeatureCatchable,
  isFeatureVisible,
  isWithinZoom,
  overlayImageUrl,
  STAND_IN_KEY
} from 'src/utils/map/feature_style'

/**
 * The invisible layer that catches clicks.
 *
 * A line and a point need the wide stroke to be catchable, and the point radius is derived from
 * that same weight in `src/constants/map.ts` so the two cannot drift apart.
 *
 * An area does not: it catches over its own surface, and the wide stroke would push its edge
 * outwards, far enough that a click inside one project could open its neighbour. So an area gets
 * exactly the stroke it shows.
 *
 * A GeometryCollection takes the wide stroke, because the lines it may hold would otherwise be
 * hard to hit. Areas inside one keep the outward edge.
 */
const hitAreaStyle = (feature?: Feature): PathOptions => {
  const type = feature?.geometry?.type
  const isArea = type === 'Polygon' || type === 'MultiPolygon'
  return {
    opacity: 0,
    fillOpacity: 0,
    weight: isArea ? (featurePathOptions(feature).weight ?? 0) : MAP_HIT_AREA_WEIGHT
  }
}

// A pane of its own, above the one the shapes share and above the markers, so the stand-in stays
// clickable where two projects overlap. The number is read against whichever stylesheet the map
// loads, and the two in use are an order of magnitude apart: `mapbox.css`, which the maps import,
// numbers the panes from 2 to 8, while `leaflet.css` numbers them from 200 to 700. Against
// `leaflet.css` this does not clear the whole ladder, and it does not have to: it sits above the
// shapes and the markers in both, which is what the stand-in needs.
const STAND_IN_PANE = 'lopo-stand-in'
const STAND_IN_PANE_Z_INDEX = 650

interface IProps {
  geojson: GeoJsonObject;
  // Where a click goes. Without it the layer is drawn but does not react, which is what the
  // project's own map does.
  url?: string;
  // Where no feature of a project is visible, a point takes its place so the project stays
  // findable. Every map sets this.
  standInWhenEmpty?: boolean;
}

const featuresOf = (geojson: GeoJsonObject): Feature[] => {
  if (geojson.type === 'FeatureCollection') {
    return (geojson as FeatureCollection).features
  }
  if (geojson.type === 'Feature') {
    return [geojson as Feature]
  }
  // A bare geometry. It carries no properties, so nothing can hide it.
  return []
}

/**
 * Which features are drawn at this zoom, by their position in the document.
 *
 * This is what the layer is keyed on, and it is a set rather than a count on purpose. Zooming can
 * exchange one feature for another while their number stays the same: one leaves at its `maxzoom`
 * as the next arrives at its `minzoom`. A key counting them would not change, and react-leaflet
 * 4.2.1 updates only `style` on a layer it keeps (`updateGeoJSON` in its `GeoJSON.js`) - `data`
 * and `filter` are read while the layer is built. The map would go on showing the feature that
 * should have left.
 *
 * Exported so that this can be tested without rendering a map.
 */
export const visibleIndices = (
  features: Feature[],
  zoom: number
): number[] => features.reduce<number[]>((found, feature, index) => {
  if (isFeatureVisible(feature, zoom)) {
    found.push(index)
  }
  return found
}, [])

// A picture to draw over a feature's box, and the place in the document the feature holds.
export interface IOverlay {
  index: number;
  url: string;
  bounds: L.LatLngBounds;
}

/**
 * The box a picture fills, or nothing where there is no box to fill.
 *
 * Read through Leaflet rather than from the coordinates, so the picture lands exactly where the
 * same feature would have been drawn as a shape.
 *
 * A box has to have width and height. A carrier that is a single point, or whose corners all
 * coincide, gives valid bounds that cover nothing: the picture would be invisible and would still
 * count as drawn, and the project would lose the point that stands in for it. The server does not
 * refuse such a carrier, and hand editing a document is how the first ones will arrive.
 */
export const overlayBounds = (feature: Feature): L.LatLngBounds | undefined => {
  let bounds: L.LatLngBounds
  try {
    bounds = L.geoJSON(feature as GeoJSON.GeoJsonObject).getBounds()
  } catch {
    // A geometry Leaflet cannot read. One feature must not take the whole map with it, and an
    // error here leaves a useEffect and reaches the app's error boundary.
    return undefined
  }

  if (!bounds.isValid()) {
    return undefined
  }

  const hasExtent = bounds.getNorth() > bounds.getSouth() && bounds.getEast() > bounds.getWest()
  return hasExtent ? bounds : undefined
}

/**
 * The pictures a document draws at this zoom.
 *
 * Each keeps the place its feature holds in the document, which three things need: the feature
 * itself is looked up by it, the layer is held under it so the ordering can reach it again, and it
 * goes into the key that decides when a layer is rebuilt. Numbering the drawn ones instead would
 * keep them in the right order among each other and still lose all three.
 *
 * `failed` holds the addresses the browser could not load. Such a picture has to leave the list,
 * or the project counts it as drawn and shows neither a picture nor its stand-in point.
 *
 * Exported so that this can be tested without rendering a map.
 */
export const visibleOverlays = (
  features: Feature[],
  zoom: number,
  failed: ReadonlySet<string>
): IOverlay[] => features.reduce<IOverlay[]>((found, feature, index) => {
  const url = overlayImageUrl(feature)
  if (!url || failed.has(url) || !isWithinZoom(feature?.properties, zoom)) {
    return found
  }

  const bounds = overlayBounds(feature)
  if (bounds) {
    found.push({ index, url, bounds })
  }
  return found
}, [])

/**
 * The key that decides when the layer is rebuilt.
 *
 * Its own function so that a spec can watch this line: turning either set back into a count reads
 * harmlessly and puts the fault above right back.
 *
 * The pictures belong in it as much as the shapes do. They are not drawn by these layers, but the
 * hit layer carries a carrier whose picture is drawn, and `filter` is read only while a layer is
 * built. Without the pictures in the key, a zoom step that exchanges a picture for a shape leaves
 * the hit layer standing on the previous zoom.
 */
export const layerKeyOf = (
  visible: number[],
  overlays: number[],
  showStandIn: boolean
): string => (showStandIn ? 'stand-in' : `${visible.join(',')}|${overlays.join(',')}`)

const eachPosition = (coordinates: unknown, visit: (position: Position) => void): void => {
  if (!Array.isArray(coordinates)) {
    return
  }
  if (typeof coordinates[0] === 'number') {
    visit(coordinates as unknown as Position)
    return
  }
  coordinates.forEach((part) => eachPosition(part, visit))
}

/**
 * The middle of a document, for the stand-in point.
 *
 * Computed here rather than read from `Project.boundingBox`: the overview loads its projects
 * through `ProjectPreviewFragment`, and that fragment carries `geojson` but no bounding box.
 *
 * `deko` features are left out, the way the backend leaves them out of where a record *is*
 * (`COUNTING_FEATURES`). Its bounding box is a different selection and keeps the ones carrying a
 * picture, because a map fitted to it would otherwise cut the picture off — that rule lives in
 * `src/utils/map/geojson_bounds.ts`. If leaving them out leaves nothing, every feature counts, so
 * a document made only of decoration still gets a point.
 */
export const geojsonCenter = (geojson: GeoJsonObject): [number, number] | undefined => {
  const features = featuresOf(geojson)
  // Strictly true, the way the backend reads it when it builds a bounding box. Read as truthy, a
  // `deko: "false"` would count as decoration here and not there.
  const counting = features.filter((feature) => (feature.properties ?? {}).deko !== true)
  const considered = counting.length > 0 ? counting : features

  let minLat = Number.POSITIVE_INFINITY
  let maxLat = Number.NEGATIVE_INFINITY
  let minLng = Number.POSITIVE_INFINITY
  let maxLng = Number.NEGATIVE_INFINITY

  const visit = (position: Position) => {
    const [lng, lat] = position
    minLat = Math.min(minLat, lat)
    maxLat = Math.max(maxLat, lat)
    minLng = Math.min(minLng, lng)
    maxLng = Math.max(maxLng, lng)
  }

  // A GeometryCollection carries `geometries` instead of `coordinates`; without this a project
  // made of one would get no centre, and then no stand-in point either.
  const visitGeometry = (geometry?: Geometry): void => {
    if (!geometry) {
      return
    }
    if (geometry.type === 'GeometryCollection') {
      geometry.geometries.forEach(visitGeometry)
      return
    }
    if ('coordinates' in geometry) {
      eachPosition(geometry.coordinates, visit)
    }
  }

  if (considered.length > 0) {
    considered.forEach((feature) => visitGeometry(feature.geometry))
  } else {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    eachPosition((geojson as any).coordinates, visit)
  }

  if (minLat > maxLat) {
    return undefined
  }

  return [(minLat + maxLat) / 2, (minLng + maxLng) / 2]
}

const standInFeature = (center: [number, number]): Feature => ({
  type: 'Feature',
  geometry: { type: 'Point', coordinates: [center[1], center[0]] },
  properties: {
    [STAND_IN_KEY]: true,
    // Written as simplestyle so the shared style function produces it, like any other feature.
    stroke: MAP_STAND_IN_POINT_OPTIONS.color,
    'stroke-width': MAP_STAND_IN_POINT_OPTIONS.weight,
    'stroke-opacity': MAP_STAND_IN_POINT_OPTIONS.opacity,
    fill: MAP_STAND_IN_POINT_OPTIONS.fillColor,
    'fill-opacity': MAP_STAND_IN_POINT_OPTIONS.fillOpacity
  }
})

/**
 * The area one ring encloses, by the shoelace formula, in square degrees.
 *
 * Square degrees are not an area on the ground: a degree of longitude is shorter than one of
 * latitude, and by how much depends on where you are. That does not matter here, because the
 * numbers are only ever compared with each other within one map, where the same distortion
 * applies to every shape.
 */
const ringArea = (ring: L.LatLng[]): number => {
  let sum = 0
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    sum += (ring[j].lng + ring[i].lng) * (ring[j].lat - ring[i].lat)
  }
  return Math.abs(sum / 2)
}

// The first ring is the outline, every further one is a hole and comes off again. A ring drawn
// around a large hole covers little, and counting the hole as covered would push it down under
// the shapes it barely touches.
const polygonArea = (rings: L.LatLng[][]): number => rings.reduce(
  (sum, ring, index) => (index === 0 ? ringArea(ring) : sum - ringArea(ring)),
  0
)

/**
 * What a layer actually covers. Leaflet nests this by type: a ring for a polyline, rings for a
 * polygon, rings of rings for a multi polygon. An empty geometry gives an empty list and counts
 * as nothing, which is also what keeps it away from anything that would throw.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const coveredArea = (latlngs: any): number => {
  if (!Array.isArray(latlngs) || latlngs.length === 0) {
    return 0
  }
  if (!Array.isArray(latlngs[0])) {
    return ringArea(latlngs as L.LatLng[])
  }
  if (!Array.isArray(latlngs[0][0])) {
    return Math.max(0, polygonArea(latlngs as L.LatLng[][]))
  }
  return (latlngs as L.LatLng[][][]).reduce(
    (sum, part) => sum + Math.max(0, polygonArea(part)),
    0
  )
}

// One ordering per map and frame. Every FeatureLayer asks for it after its own rebuild, and on a
// map with many projects that is once per project, each time walking every layer. Collecting the
// requests turns N passes per render into one.
const orderingPending = new WeakSet<L.Map>()

const scheduleOrdering = (map: L.Map): void => {
  if (orderingPending.has(map)) {
    return
  }
  orderingPending.add(map)
  requestAnimationFrame(() => {
    orderingPending.delete(map)
    // The map may be gone by the time the frame comes round, after a page change. Asked by its
    // panes, not by its container: `remove()` clears the panes but leaves the container in
    // place, so a check on that one would never stop anything.
    if (map.getPane('overlayPane')) {
      putSmallerAreasOnTop(map)
    }
  })
}

// What each path carries so the ordering can place it: how much ground its project covers, which
// project it belongs to, and where its shape stands in the document.
const PROJECT_SIZE = '_lopoProjectSize'
const PROJECT_ID = '_lopoProjectId'
const DOCUMENT_INDEX = '_lopoDocumentIndex'

let nextProjectId = 0

/**
 * Every path under a layer, however deep.
 *
 * One feature is not always one path: Leaflet turns a MultiPoint into a group of circles and a
 * GeometryCollection into a group of its parts. Walking only the direct children would leave
 * those unmarked, and an unmarked path is never moved, so it would sink under every area.
 */
const eachPath = (layer: L.Layer, visit: (path: L.Layer) => void): void => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const candidate = layer as any
  if (candidate._path) {
    visit(layer)
    return
  }
  if (typeof candidate.eachLayer === 'function') {
    candidate.eachLayer((child: L.Layer) => eachPath(child, visit))
  }
}

/**
 * How much ground a project covers, as the sum of its areas.
 *
 * Only areas count. The shoelace formula closes whatever it is given, so an L shaped street would
 * enclose the triangle to its own chord and a curved one a sliver along its length, and either
 * could sink under a small area. A project made only of lines comes out at nothing and ends up on
 * top, which is right: a line is the smaller target.
 */
export const projectArea = (group: L.GeoJSON | null): number => {
  let sum = 0
  group?.eachLayer((child: L.Layer) => eachPath(child, (path: L.Layer) => {
    if (path instanceof L.Polygon) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sum += coveredArea((path as any).getLatLngs?.())
    }
  }))
  return sum
}

/**
 * The ground a document's pictures cover, in the square degrees `ringArea` counts, so the two add
 * up to one size for the project.
 *
 * A box is axis aligned, and the shoelace formula over its four corners is exactly its width times
 * its height, which is why the multiplication here and the formula there agree.
 *
 * A project made only of pictures would otherwise come out at nothing and sort to the very top,
 * where it takes the click from every smaller project lying inside its picture.
 */
export const overlayArea = (overlays: IOverlay[]): number => overlays.reduce(
  (sum, { bounds }) => {
    const height = bounds.getNorth() - bounds.getSouth()
    const width = bounds.getEast() - bounds.getWest()
    return sum + height * width
  },
  0
)

// Marks every path of one project, in the order its document lists the shapes. All the paths of
// one feature share its index, so a MultiPoint stays one shape in that order.
export const markProject = (group: L.GeoJSON | null, size: number, id: number): void => {
  let index = 0
  group?.eachLayer((child: L.Layer) => {
    eachPath(child, (path: L.Layer) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const marked = path as any
      marked[PROJECT_SIZE] = size
      marked[PROJECT_ID] = id
      marked[DOCUMENT_INDEX] = index
    })
    index += 1
  })
}

/**
 * Marks the pictures of one project so the ordering can place them among each other.
 *
 * They carry the place their feature holds in the document, where `markProject` counts positions
 * among the shapes it is given. Either would put the pictures in the same order among each other,
 * since both rise with the document; the document's own number is used because that is what the
 * pictures are held and looked up under everywhere else in this file.
 */
export const markOverlays = (
  layers: Map<number, L.ImageOverlay>,
  overlays: IOverlay[],
  size: number,
  id: number
): void => {
  overlays.forEach(({ index }) => {
    const layer = layers.get(index)
    if (!layer) {
      return
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const marked = layer as any
    marked[PROJECT_SIZE] = size
    marked[PROJECT_ID] = id
    marked[DOCUMENT_INDEX] = index
  })
}

/**
 * Puts the smaller project on top, for the eye and for the click.
 *
 * Without this the click goes to whichever project the list happened to put last, so a project
 * drawn inside another one cannot be reached: the larger one covers it, whether it is filled or
 * only outlined. Drawing follows the same order, because an area covering another one opaquely
 * would otherwise take a click for a project nobody can see.
 *
 * Between projects, not inside one. Within a document the shapes keep the order their author gave
 * them, the way the CMS draws them: a deck over its shadow, piers under the deck. Sorting those by
 * size lays the piers across the road, and it gains nothing, because every click inside one
 * document opens the same project anyway.
 *
 * Only paths this component marked are moved, so a location outline or anything else on the map
 * keeps its place.
 */
export const putSmallerAreasOnTop = (map: L.Map): void => {
  const drawn: L.Layer[] = []
  const catching: L.Layer[] = []
  const pictures: L.Layer[] = []

  map.eachLayer((layer: L.Layer) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const path = layer as any
    if (path[PROJECT_SIZE] === undefined) {
      return
    }
    // A picture is an `<img>` and carries `_image` where a shape carries `_path`. It is sorted in
    // a list of its own because it cannot interleave with the shapes anyway: every shape of a map
    // lives in one `<svg>`, and the pictures lie above that whole element. Sorting them together
    // would promise an order the drawing cannot keep.
    if (path._image) {
      pictures.push(layer)
      return
    }
    if (!path._path) {
      return
    }
    if (path.options?.interactive) {
      catching.push(layer)
    } else {
      drawn.push(layer)
    }
  })

  // Largest project first, and inside a project the document order: each call moves its layer to
  // the end, so the last one moved ends up on top. The drawn layers go first and the catching
  // ones after them, which keeps the two orders apart instead of interleaving them. It makes no
  // difference to the click itself, since Leaflet gives a drawn path `pointer-events: none` and
  // it takes nothing wherever it lies.
  // Project by size, then the project itself, then the document. Without the middle term two
  // projects of the same size interleave, and projects made only of lines or points all sit at
  // zero and would shuffle into each other.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const order = (a: any, b: any) => (b[PROJECT_SIZE] - a[PROJECT_SIZE]) ||
    (a[PROJECT_ID] - b[PROJECT_ID]) ||
    (a[DOCUMENT_INDEX] - b[DOCUMENT_INDEX])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const toFront = (layer: L.Layer) => (layer as any).bringToFront()
  drawn.sort(order).forEach(toFront)
  catching.sort(order).forEach(toFront)
  // The pictures among themselves. They share one z-index, so what decides between them is the
  // order they stand in inside their pane, and `bringToFront` moves an image overlay there the
  // same way it moves a path inside its `<svg>`.
  pictures.sort(order).forEach(toFront)
}

const FeatureLayer: React.FC<IProps> = ({
  geojson,
  url,
  standInWhenEmpty = false
}) => {
  const history = useHistory()
  const map = useMap()
  const [zoom, setZoom] = useState<number>(map.getZoom())
  const visibleLayer = useRef<L.GeoJSON | null>(null)
  const hitLayer = useRef<L.GeoJSON | null>(null)
  // The image overlays this project has on the map, by the place their feature holds in the
  // document. Kept so the ordering can reach them: they are not children of either GeoJSON layer.
  const pictureLayers = useRef<Map<number, L.ImageOverlay>>(new Map())
  // Addresses the browser could not load. A picture that does not arrive has to stop counting as
  // drawn, otherwise the map shows neither it nor the point that would have stood in for it, and
  // the two failures look exactly alike on the screen.
  const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set())
  const rememberFailure = useCallback((address: string) => {
    setFailed((known) => (known.has(address) ? known : new Set(known).add(address)))
  }, [])
  // A number that keeps this project together across renders. Handed out from a module counter
  // while rendering, so StrictMode may skip one; only being unique matters.
  const projectId = useRef<number>(0)
  if (projectId.current === 0) {
    nextProjectId += 1
    projectId.current = nextProjectId
  }

  // The stand-in has to sit above every other project, including their hit areas: two projects
  // can overlap, and the point is the only thing left of this one.
  //
  // Built while rendering, not in an effect: Leaflet resolves the pane when it adds the layer, and
  // the children below mount before this component's own effects run.
  useMemo(() => {
    if (!map.getPane(STAND_IN_PANE)) {
      const pane = map.createPane(STAND_IN_PANE)
      pane.style.zIndex = `${STAND_IN_PANE_Z_INDEX}`
    }
  }, [map])

  // Kept stable: react-leaflet re-subscribes whenever this object changes identity, and that
  // would happen on every render of every project.
  const mapHandlers = useMemo(() => ({
    zoomend: () => setZoom(map.getZoom())
  }), [map])
  useMapEvents(mapHandlers)

  const features = useMemo(() => featuresOf(geojson), [geojson])
  const visible = useMemo(() => visibleIndices(features, zoom), [features, zoom])
  const overlays = useMemo(
    () => visibleOverlays(features, zoom, failed),
    [features, zoom, failed]
  )

  // A bare geometry has no features to count and nothing that could hide it. A document whose
  // only drawable features are pictures has something to show, so it keeps its place on the map
  // rather than falling back to the stand-in point.
  const nothingVisible = features.length > 0 && visible.length === 0 && overlays.length === 0
  const center = useMemo(
    () => (standInWhenEmpty && nothingVisible ? geojsonCenter(geojson) : undefined),
    [standInWhenEmpty, nothingVisible, geojson]
  )
  const showStandIn = !!center

  const data = useMemo(
    () => (center ? (standInFeature(center) as unknown as GeoJsonObject) : geojson),
    [center, geojson]
  )

  // `filter` is only read while the layer is built, so the layer has to be rebuilt when the set of
  // visible features changes. Keyed on the set itself rather than on the zoom: with the zoom in
  // the key every project would rebuild both its layers on every step, including the many that
  // carry no zoom keys at all.
  const layerKey = layerKeyOf(visible, overlays.map(({ index }) => index), showStandIn)

  const filter = useCallback(
    // Leaflet hands `filter` the raw object, before it wraps a bare geometry into a feature.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (feature: any) => isFeatureVisible(feature, zoom),
    [zoom]
  )

  // Which features carry a picture right now. Held as the feature objects themselves: Leaflet
  // hands `filter` the very objects the document is made of, so identity answers the question
  // without a second pass over the properties.
  const drawnCarriers = useMemo(
    () => new Set<unknown>(overlays.map(({ index }) => features[index])),
    [overlays, features]
  )

  // The hit layer leaves out what the author hid: an invisible feature would otherwise keep an
  // invisible hit area, and a click would land on something nobody can see. A carrier whose
  // picture is drawn belongs in it for the opposite reason: the picture is the only thing a
  // visitor sees of that feature, and without this a project made of pictures would show them and
  // answer no click at all.
  const hitFilter = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (feature: any) => (isFeatureVisible(feature, zoom) || drawnCarriers.has(feature)) &&
      isFeatureCatchable(feature),
    [zoom, drawnCarriers]
  )

  // After every rebuild: the layers only reach the map once the children below have rendered.
  // This runs on every render on purpose, so a new project arriving or a zoom step rebuilding
  // the layers marks and reorders them again; the scheduling keeps the ordering itself down to
  // one pass per frame.
  useEffect(() => {
    // The pictures count towards the ground the project covers. A project made only of pictures
    // would otherwise measure nothing, sort above everything and take the click from every
    // smaller project lying inside it.
    const size = projectArea(visibleLayer.current) + overlayArea(overlays)
    markProject(visibleLayer.current, size, projectId.current)
    markProject(hitLayer.current, size, projectId.current)
    markOverlays(pictureLayers.current, overlays, size, projectId.current)
    scheduleOrdering(map)
  })

  const style = useCallback((feature?: Feature) => featurePathOptions(feature), [])

  // Leaflet calls pointToLayer without the layer's options (`_pointToLayer` in leaflet-src.js), so
  // the pane set on the GeoJSON layer never reaches a point. The stand-in is always a point:
  // without this it is drawn into the overlay pane, where an overlapping project can cover it.
  const pointPane = useMemo(() => (showStandIn ? { pane: STAND_IN_PANE } : {}), [showStandIn])

  const pointToLayer = useCallback((feature: Feature, latlng: LatLngExpression) => {
    const properties = feature.properties ?? {}

    // What carries: the radius, that this circle must not take the click, and the pane. Colour and
    // fill are spread in so the circle starts out right, but the style function is what decides
    // them — Leaflet runs it over this layer again right after building it.
    return L.circleMarker(latlng, {
      ...featurePathOptions(feature),
      radius: properties[STAND_IN_KEY] ? MAP_STAND_IN_POINT_OPTIONS.radius : MAP_POINT_RADIUS,
      interactive: false,
      ...pointPane
    })
  }, [pointPane])

  const hitAreaPointToLayer = useCallback(
    (feature: Feature, latlng: LatLngExpression) => L.circleMarker(latlng, {
      ...hitAreaStyle(feature),
      radius: MAP_POINT_HIT_AREA_RADIUS,
      interactive: true,
      ...pointPane
    }),
    [pointPane]
  )

  const onEachFeature = useCallback((_feature: Feature, layer: Layer) => {
    if (!url) {
      return
    }
    layer.on({
      mouseover: () => {
        // The whole project lights up, each feature on its own colours.
        visibleLayer.current?.eachLayer((child) => {
          // A FeatureGroup from a GeometryCollection has setStyle too; a marker has none.
          if ('setStyle' in child) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const path = child as any
            path.setStyle(featureHoverOptions(path.feature))
          }
        })
      },
      mouseout: () => {
        // Leaflet puts every layer back on `options.style`, which is the style function above.
        visibleLayer.current?.resetStyle()
      },
      click: () => history.push(url)
    })
  }, [history, url])

  // Held onto while the overlay is on the map and dropped when react-leaflet takes it off, so the
  // ordering never reaches for a layer the map no longer has.
  const holdPicture = useCallback((index: number, layer: L.ImageOverlay | null) => {
    if (layer) {
      pictureLayers.current.set(index, layer)
    } else {
      pictureLayers.current.delete(index)
    }
  }, [])

  return (
    <>
      {/* The picture is embedded as an image and nothing else. An `<img>` runs no script from an
          svg, whatever the file brings; fetching the markup and putting it into the page, or
          reaching for `<object>` or `<iframe>`, would run it. The box comes from the feature's own
          geometry, so the picture lands where the shape would have been drawn. `alt` is empty on
          purpose: the drawing is decoration over a map that carries no text of its own, and the
          project is named beside it. */}
      {overlays.map((overlay) => (
        <ImageOverlay
          key={`picture-${overlay.index}-${overlay.url}`}
          ref={(layer) => holdPicture(overlay.index, layer)}
          url={overlay.url}
          bounds={overlay.bounds}
          zIndex={MAP_OVERLAY_IMAGE_Z_INDEX}
          alt=''
          interactive={false}
          eventHandlers={{ error: () => rememberFailure(overlay.url) }}
        />
      ))}
      <GeoJSON
        key={`visible-${layerKey}`}
        ref={visibleLayer}
        data={data as GeoJSON.GeoJsonObject}
        style={style}
        filter={filter}
        pointToLayer={pointToLayer}
        interactive={false}
        pane={showStandIn ? STAND_IN_PANE : undefined}
      />
      {url && (
        <GeoJSON
          key={`hit-${layerKey}`}
          ref={hitLayer}
          data={data as GeoJSON.GeoJsonObject}
          style={hitAreaStyle}
          filter={hitFilter}
          pointToLayer={hitAreaPointToLayer}
          interactive
          onEachFeature={onEachFeature}
          pane={showStandIn ? STAND_IN_PANE : undefined}
        />
      )}
    </>
  )
}

export default FeatureLayer
